import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ToolCard } from "@/components/tools/ToolCard";
import { TOOLS, TOOL_CATEGORIES } from "@/constants/tools";
import { Container } from "@/components/Layout/Container";
import { PrimaryHeader } from '@/components/Layout/Header/PrimaryHeader';

export function ToolOverview() {
  const navigate = useNavigate();

  // Memoize grouped tools to prevent recalculation on every render
  const groupedTools = useMemo(() => 
    TOOL_CATEGORIES.reduce((acc, category) => ({
      ...acc,
      [category.id]: TOOLS.filter(tool => tool.category === category.id)
    }), {} as Record<string, typeof TOOLS>),
  []);
  
  // Memoize navigation handler
  const handleLaunch = useCallback(
    (path: string) => navigate(path),
    [navigate]
  );

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <PrimaryHeader />

      <main className="flex-grow">
        <Container>
          <section className="py-8">
            <PageHeader />
            
            {TOOL_CATEGORIES.map((category) => (
              <ToolCategorySection
                key={category.id}
                category={category}
                tools={groupedTools[category.id]}
                onLaunch={handleLaunch}
              />
            ))}
          </section>
        </Container>
      </main>
    </div>
  );
}

// Extracted components for better readability and potential reuse
const PageHeader = () => (
  <header className="mb-8">
    <h1 className="text-3xl font-bold mb-2">Quantity Surveyor Tools</h1>
    <p className="text-muted-foreground">
      Access our suite of specialised tools designed to help you manage your projects effectively.
    </p>
  </header>
);

type ToolCategorySectionProps = {
  category: typeof TOOL_CATEGORIES[number];
  tools: typeof TOOLS;
  onLaunch: (path: string) => void;
};

const ToolCategorySection = ({ category, tools, onLaunch }: ToolCategorySectionProps) => (
  <section className="mb-12">
    <header className="mb-6">
      <h2 className="text-2xl font-semibold mb-2">{category.name}</h2>
      <p className="text-muted-foreground">{category.description}</p>
    </header>

    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {tools.map((tool) => (
        <ToolCard
          key={tool.id}
          tool={tool}
          onLaunch={onLaunch}
        />
      ))}
    </div>
  </section>
);